/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import XlbutorHome from "./sections/XlbutorHome.jsx";
import ProductsPage from "./pages/ProductsPage.jsx";
import WorksPage from "./pages/WorksPage.jsx";
import MapFooter from "./components/MapFooter.jsx";
import ContactPage from "./pages/ContactPage.jsx";
import About from "./pages/About.jsx";
import AdminLogin from "./pages/AdminLogin.jsx";
import AdminDashboard from "./pages/AdminDashboard.jsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.jsx";
import Impresszum from "./pages/Impresszum.jsx";
import ASZF from "./pages/ASZF.jsx";
import CookiePolicy from "./pages/CookiePolicy.jsx";
import ModernHomePage from "./pages/ModernHomePage.jsx";
import ModernAboutPage from "./pages/modern/ModernAboutPage.jsx";
import ModernContactPage from "./pages/modern/ModernContactPage.jsx";
import ModernImpreszum from "./pages/modern/ModernImpreszum.jsx";
import ModernASZF from "./pages/modern/ModernASZF.jsx";
import ModernAdatkezeles from "./pages/modern/ModernAdatkezeles.jsx";
import Palyazat from "./pages/Palyazat.jsx";
import ScrollToTop from "./components/ScrollToTop.jsx";

/**
 * XL bútor UI recreation (static mock)
 * - TailwindCSS for styling
 * - Single-file React component with small component library
 */

// ----------------------------- layout components -----------------------------
const Container = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-[1200px] px-4 ${className}`}>{children}</div>
);

const Header = ({ current, onNavigate }) => {
  const [clickCount, setClickCount] = React.useState(0);
  const [clickTimeout, setClickTimeout] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  // Logo click handler
  const handleLogoClick = () => {
    setClickCount(prev => prev + 1);
    
    if (clickTimeout) clearTimeout(clickTimeout);
    
    const timeout = setTimeout(() => {
      if (clickCount + 1 === 3) {
        window.open('/dashboard', '_blank');
      } else {
        onNavigate('home');
        setTimeout(() => {
          window.scrollTo({ 
            top: 0, 
            behavior: 'smooth'
          });
        }, 100);
      }
      setClickCount(0);
    }, 500);
    
    setClickTimeout(timeout);
  };

  // Toggle mobile menu with smooth animation
  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      // Closing: animate out first, then remove
      setIsAnimating(false);
      setTimeout(() => {
        setMobileMenuOpen(false);
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }, 300);
    } else {
      // Opening: render first, then animate in
      // Scroll-lock with scrollbar compensation
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      setMobileMenuOpen(true);
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    }
  };

  // Keyboard handlers (Esc to close, Tab focus trap)
  React.useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        toggleMobileMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  // Navigate and close menu
  const navigateAndClose = (page) => {
    setIsAnimating(false);
    setTimeout(() => {
      onNavigate(page);
      setMobileMenuOpen(false);
      document.body.style.overflow = '';
    }, 300);
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-gray-900/80 backdrop-blur supports-[backdrop-filter]:bg-gray-900/70 border-b border-gray-700/30">
      <Container className="flex items-center justify-between py-3 md:py-4">
        {/* Logo */}
        <div 
          className="cursor-pointer select-none" 
          onClick={handleLogoClick}
          title="Kattints a főoldalhoz | Tripla kattintás az admin felülethez"
        >
          <img 
            src="/images/Weboldalképei/xl-butor-logo.png" 
            alt="XL bútor" 
            className="h-6 sm:h-8 w-auto"
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 text-[14px] tracking-wide text-white">
          <button
            onClick={() => {
              onNavigate("home");
              setTimeout(() => {
                window.scrollTo({ 
                  top: 0, 
                  behavior: 'smooth'
                });
              }, 100);
            }}
            className="uppercase transition-colors hover:text-gray-300 text-white"
          >
            FŐOLDAL
          </button>

          <button onClick={() => {
            onNavigate("rolunk");
            setTimeout(() => {
              window.scrollTo({ 
                top: 0, 
                behavior: 'smooth'
              });
            }, 100);
          }} className="uppercase transition-colors hover:text-gray-300 text-white">
            RÓLUNK
          </button>


          <button onClick={() => {
            onNavigate("munkaink");
            setTimeout(() => {
              window.scrollTo({ 
                top: 0, 
                behavior: 'smooth'
              });
            }, 100);
          }} className="uppercase transition-colors hover:text-gray-300 text-white">
            MUNKÁINK
          </button>

        </nav>
        
        {/* Mobile Icons */}
        <div className="flex md:hidden items-center gap-3">
          {/* Hamburger Menu */}
          <button 
            onClick={toggleMobileMenu}
            className="p-2 -m-2 text-gray-300 hover:text-white transition-colors"
            aria-label="Menü"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </Container>

      {/* Mobile Menu Overlay - Simple & Clean */}
      {mobileMenuOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            opacity: isAnimating ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
          onClick={toggleMobileMenu}
        >
          <div 
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '280px',
              maxWidth: '85vw',
              height: '100vh',
              backgroundColor: 'rgba(45, 45, 45, 0.92)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '-2px 0 10px rgba(0,0,0,0.3)',
              transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #444',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: 'bold', 
                color: '#fff' 
              }}>
                Menü
              </h3>
              <button 
                onClick={toggleMobileMenu}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Menu Items */}
            <nav style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: '20px 0'
            }}>
              <button 
                onClick={() => navigateAndClose("home")}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  border: 'none',
                  background: 'none',
                  color: '#fff',
                  fontSize: '16px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Főoldal
              </button>


              <button 
                onClick={() => navigateAndClose("rolunk")}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  border: 'none',
                  background: 'none',
                  color: '#fff',
                  fontSize: '16px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Rólunk
              </button>

              <button 
                onClick={() => {
                  navigateAndClose("munkaink");
                  setTimeout(() => {
                    window.scrollTo({ 
                      top: 0, 
                      behavior: 'smooth'
                    });
                  }, 300);
                }}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  border: 'none',
                  background: 'none',
                  color: '#fff',
                  fontSize: '16px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Munkáink
              </button>
            </nav>
          </div>
        </div>
      )}

    </header>
  );
};

// ----------------------------- pages -----------------------------
const ColorsPage = () => {
  const Footer = () => (
    <footer className="bg-gray-800 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* Címünk */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-red-500">Címünk</h3>
            <p className="text-sm">Kisvárda, Attila u. 61, 4600</p>
          </div>

          {/* Nyitvatartás */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-red-500">Nyitvatartás</h3>
            <p className="text-sm">Hétfő - Péntek: 9:00-17:00</p>
            <p className="text-sm">Szombat: 8:00-13:00</p>
          </div>

          {/* Kapcsolat */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-red-500">Kapcsolat</h3>
            <div className="space-y-2 text-sm">
              <p>📞 Telefonszám: 06 (70) 417 9210</p>
              <p>✉️ xlbutorbolt@gmail.com</p>
            </div>
        </div>

      </div>
      </div>
  </footer>
);

  return (
    <div>
  <Container>
    <div className="mb-8">
      <h1 className="text-[28px] font-semibold tracking-tight text-[#333] mb-4">Szín szerint vásárolj</h1>
          <p className="text-[#777] text-[14px] leading-relaxed">
            Válassz a legnépszerűbb színek közül, hogy megtaláld a tökéletes szőnyeget otthonodhoz.
          </p>
    </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[
            { name: "Fekete", color: "bg-black", count: "24 termék" },
            { name: "Kék", color: "bg-blue-600", count: "18 termék" },
            { name: "Szürke", color: "bg-gray-500", count: "32 termék" },
            { name: "Bézs", color: "bg-amber-200", count: "28 termék" },
            { name: "Piros", color: "bg-red-600", count: "15 termék" },
            { name: "Zöld", color: "bg-green-600", count: "12 termék" },
            { name: "Fehér", color: "bg-white border border-gray-300", count: "8 termék" },
            { name: "Barna", color: "bg-amber-800", count: "22 termék" }
          ].map((item, i) => (
            <div key={i} className="group cursor-pointer">
              <div className={`${item.color} aspect-square rounded-lg mb-3 group-hover:scale-105 transition-transform`} />
              <h3 className="font-medium text-[#333] mb-1">{item.name}</h3>
              <p className="text-[12px] text-[#777]">{item.count}</p>
        </div>
      ))}
    </div>
  </Container>
      <Footer />
    </div>
);
};

// ----------------------------- home page content -----------------------------
function HomePageContent({ onNavigate }) {
  return <XlbutorHome onNavigate={onNavigate} />;
}

// ----------------------------- root app -----------------------------
function AppContent() {
  const location = window.location;
  const [cookiesAccepted, setCookiesAccepted] = React.useState(false);

  // Check if cookies were already accepted
  React.useEffect(() => {
    const accepted = localStorage.getItem('cookiesAccepted');
    if (accepted === 'true') {
      setCookiesAccepted(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setCookiesAccepted(true);
  };

      // URL-ből meghatározzuk az aktuális oldalt
      const getCurrentPage = () => {
        const path = location.pathname;
        if (path === '/products') return 'products';
        if (path === '/about' || path === '/rolunk') return 'about';
        if (path === '/contact') return 'contact';
        if (path === '/colors') return 'colors';
        if (path === '/map') return 'map';
        if (path === '/munkaink') return 'munkaink';
        if (path === '/adatvedelmi-nyilatkozat') return 'privacy';
        if (path === '/impresszum') return 'impresszum';
        if (path === '/aszf') return 'aszf';
        if (path === '/cookie-szabalyzat') return 'cookies';
        return 'home';
      };

  const [page, setPage] = useState(getCurrentPage());

  const handleNavigate = (newPage) => {
    setPage(newPage);
    // URL frissítése
        const pathMap = {
          'home': '/',
          'rolunk': '/rolunk',
          'munkaink': '/munkaink',
          'products': '/products',
          'about': '/about',
          'contact': '/contact',
          'colors': '/colors',
          'map': '/map',
          'privacy': '/adatvedelmi-nyilatkozat',
          'impresszum': '/impresszum',
          'aszf': '/aszf',
          'cookies': '/cookie-szabalyzat'
        };
    const newPath = pathMap[newPage] || '/';
    window.history.pushState({}, '', newPath);
  };

      const pageView = useMemo(() => {
        switch (page) {
          case "home":
                   return <HomePageContent onNavigate={handleNavigate} />;
          case "rolunk":
                   return <About />;
          case "munkaink":
            return <WorksPage />;
          case "colors":
            return <ColorsPage />;
                 case "products":
                   return <ProductsPage />;
                 case "map":
                   return <MapFooter />;
                 case "contact":
                   return <ContactPage />;
                 case "about":
                   return <About />;
          case "privacy":
            return <PrivacyPolicy />;
          case "impresszum":
            return <Impresszum />;
          case "aszf":
            return <ASZF />;
          case "cookies":
            return <CookiePolicy />;
          default:
                   return <HomePageContent onNavigate={handleNavigate} />;
        }
             }, [page]);

  return (
    <div className="min-h-screen bg-white text-[#222]">
      {page !== "map" && page !== "contact" && (
        <>
          {/* Top Info Bar */}
          <div className="bg-gray-800 text-white py-2 px-2 sm:px-4 text-xs sm:text-sm">
            <div className="mx-auto max-w-[1200px] flex flex-wrap items-center justify-center sm:justify-between gap-2 sm:gap-4">
              {/* Left side: Contact info */}
              <div className="flex items-center gap-3 sm:gap-6 flex-wrap justify-center sm:justify-start">
                <div className="flex items-center gap-2">
                  <a 
                    href="https://www.facebook.com/goncz.sandor.7" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:text-gray-300 transition-colors"
                    title="Facebook profil"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href="tel:06704179210" 
                    className="text-white hover:text-gray-300 transition-colors flex items-center gap-2"
                    title="Telefonálj nekünk"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <span className="hidden sm:inline">06 (70) 417 9210</span>
                    <span className="sm:hidden">Tel.</span>
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href="mailto:xlbutorbolt@gmail.com" 
                    className="text-white hover:text-gray-300 transition-colors flex items-center gap-2"
                    title="Írj nekünk emailt"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <span className="hidden sm:inline">xlbutorbolt@gmail.com</span>
                    <span className="sm:hidden">Email</span>
                  </a>
                </div>
              </div>
              {/* Right side: Opening hours */}
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="hidden sm:block">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                <span className="text-[10px] sm:text-sm">H-P: 9:00-17:00 | Szo: 8:00-13:00</span>
              </div>
            </div>
          </div>
          <Header current={page} onNavigate={handleNavigate} />
        </>
      )}
      <main className={page === "map" || page === "contact" ? "" : "pt-0 pb-8"}>{pageView}</main>
      
      {/* Cookie Consent */}
      {!cookiesAccepted && (
        <div className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-600">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Sütik használata</h3>
              <p className="text-xs text-gray-600 mb-3">
                Weboldalunk sütiket használ a jobb felhasználói élmény érdekében. A folytatással elfogadja a sütik használatát.
              </p>
                  <div className="flex gap-2">
                    <button
                      onClick={acceptCookies}
                      className="bg-gray-900 hover:bg-gray-800 text-white text-xs px-3 py-1.5 rounded transition-colors"
                    >
                      Elfogadom
                    </button>
                    <a
                      href="/cookie-szabalyzat"
                      className="text-gray-500 hover:text-gray-700 text-xs px-3 py-1.5 transition-colors"
                    >
                      Süti információk
                    </a>
                    <button
                      onClick={() => setCookiesAccepted(true)}
                      className="text-gray-500 hover:text-gray-700 text-xs px-3 py-1.5 transition-colors"
                    >
                      Bezár
                    </button>
                  </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------- main app -----------------------------
function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
               <ScrollToTop />
               <Routes>
                 {/* Modern redesigned pages (new default) */}
                 <Route path="/" element={<ModernHomePage />} />
                 <Route path="/works" element={<WorksPage />} />
                 <Route path="/about" element={<ModernAboutPage />} />
                 <Route path="/contact" element={<ModernContactPage />} />
                 <Route path="/services" element={<ModernHomePage />} />
                 
                 {/* Old route aliases (for compatibility) */}
                 <Route path="/rolunk" element={<ModernAboutPage />} />
                 <Route path="/munkaink" element={<WorksPage />} />
                 
                 {/* Legacy pages (old design) */}
                 <Route path="/old" element={<AppContent />} />
                 
                {/* Legal pages (modern) */}
                <Route path="/impresszum" element={<ModernImpreszum />} />
                <Route path="/aszf" element={<ModernASZF />} />
                <Route path="/adatkezelesi-tajekoztato" element={<ModernAdatkezeles />} />
                <Route path="/palyazat" element={<Palyazat />} />
                
                {/* Legacy legal pages (old design) */}
                <Route path="/adatvedelmi-nyilatkozat" element={<PrivacyPolicy />} />
                <Route path="/cookie-szabalyzat" element={<CookiePolicy />} />
                 
                {/* Admin routes */}
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
               </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;